import { useSettings } from "@/hooks/use-settings";
import { APP_VERSION_LABEL, APP_BUILD_DATE, APP_BUILD_HASH } from "@/lib/version";
import { useTheme } from "@/hooks/use-theme";
import { toast } from "sonner";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { HelpCircle } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import OfflineReadyBadge from "@/components/OfflineReadyBadge";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PipingSettings from "@/components/settings/PipingSettings";
import DuctworkSettings from "@/components/settings/DuctworkSettings";



const Settings = () => {
  const { settings, updateSetting, resetDefaults } = useSettings();
  const { theme, toggleTheme } = useTheme();

  const handleReset = () => {
    resetDefaults();
    toast.success("Settings reset to defaults");
  };

  return (
    <div className="pb-24 animate-fade-in">
      <PageHeader title="⚙️ Settings" subtitle="Set your default preferences for calculations" />

      <div className="px-4 mt-4 space-y-6 max-w-lg mx-auto">
        {/* Appearance */}
        <section className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Appearance</h2>
          <div className="trade-card space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-foreground">Theme</span>
                <p className="text-[11px] text-muted-foreground">Switch between dark and light mode</p>
              </div>
              <button
                onClick={toggleTheme}
                className="px-4 py-2 rounded-lg bg-secondary text-sm font-semibold text-foreground hover:bg-secondary/80 transition-colors"
              >
                {theme === "dark" ? "🌙 Dark" : "☀️ Light"}
              </button>
            </div>
          </div>
        </section>

        {/* Help & FAQ */}
        <section className="space-y-3">
          <div className="trade-card">
            <HelpLink />
          </div>
        </section>

        {/* Electrical Defaults */}
        <section className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Electrical Defaults</h2>
          <div className="trade-card space-y-4">
            <SettingRow label="Conductor Material" description="Default for wire sizing and service calcs">
              <Select value={settings.conductorMaterial} onValueChange={(v) => updateSetting("conductorMaterial", v as "cu" | "al")}>
                <SelectTrigger className="w-32 sm:w-36 bg-secondary border-border font-mono-data text-xs sm:text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cu">Copper (Cu)</SelectItem>
                  <SelectItem value="al">Aluminum (Al)</SelectItem>
                </SelectContent>
              </Select>
            </SettingRow>

            <SettingRow label="Default Voltage" description="Starting voltage for calculators">
              <Select value={settings.voltage} onValueChange={(v) => updateSetting("voltage", v)}>
                <SelectTrigger className="w-36 bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="120">120V</SelectItem>
                  <SelectItem value="208">208V</SelectItem>
                  <SelectItem value="240">240V</SelectItem>
                  <SelectItem value="277">277V</SelectItem>
                  <SelectItem value="480">480V</SelectItem>
                  <SelectItem value="600">600V</SelectItem>
                </SelectContent>
              </Select>
            </SettingRow>

            <SettingRow label="Default Phases" description="Single-phase or three-phase">
              <Select value={settings.phases} onValueChange={(v) => updateSetting("phases", v as "1" | "3")}>
                <SelectTrigger className="w-36 bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1Φ Single</SelectItem>
                  <SelectItem value="3">3Φ Three</SelectItem>
                </SelectContent>
              </Select>
            </SettingRow>

            <SettingRow label="Wire Insulation" description="Default insulation type for sizing">
              <Select value={settings.wireInsulation} onValueChange={(v) => updateSetting("wireInsulation", v as "thhn" | "thwn" | "xhhw")}>
                <SelectTrigger className="w-36 bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="thhn">THHN</SelectItem>
                  <SelectItem value="thwn">THWN-2</SelectItem>
                  <SelectItem value="xhhw">XHHW-2</SelectItem>
                </SelectContent>
              </Select>
            </SettingRow>

            <SettingRow label="Raceway Type" description="Default conduit type for fill calcs">
              <Select value={settings.racewayType} onValueChange={(v) => updateSetting("racewayType", v as any)}>
                <SelectTrigger className="w-36 bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="emt">EMT</SelectItem>
                  <SelectItem value="imc">IMC</SelectItem>
                  <SelectItem value="rgs">Rigid (RGS)</SelectItem>
                  <SelectItem value="pvc40">PVC Sch 40</SelectItem>
                  <SelectItem value="pvc80">PVC Sch 80</SelectItem>
                </SelectContent>
              </Select>
            </SettingRow>
          </div>
        </section>

        {/* Piping Defaults */}
        <PipingSettings settings={settings} updateSetting={updateSetting} />

        {/* Ductwork Defaults */}
        <DuctworkSettings settings={settings} updateSetting={updateSetting} />

        {/* Units */}
        <section className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Units</h2>
          <div className="trade-card">
            <SettingRow label="Unit System" description="Imperial (in, ft, °F) or Metric (mm, m, °C)">
              <Select value={settings.unitSystem} onValueChange={(v) => updateSetting("unitSystem", v as "imperial" | "metric")}>
                <SelectTrigger className="w-36 bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="imperial">Imperial</SelectItem>
                  <SelectItem value="metric">Metric</SelectItem>
                </SelectContent>
              </Select>
            </SettingRow>
          </div>
        </section>

        {/* Reset */}
        <section className="space-y-3">
          <div className="trade-card border-destructive/20">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-foreground">Reset to Defaults</span>
                <p className="text-[11px] text-muted-foreground">Restore all settings to factory defaults</p>
              </div>
              <button
                onClick={handleReset}
                className="px-4 py-2 rounded-lg border border-destructive/30 text-sm font-semibold text-destructive hover:bg-destructive/10 transition-colors"
              >
                Reset
              </button>
            </div>
          </div>
        </section>

        <p className="text-[11px] text-muted-foreground text-center pb-4">
          Settings are saved locally on this device.
        </p>

        {/* Offline status */}
        <section className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Offline Status</h2>
          <div className="trade-card">
            <OfflineReadyBadge />
          </div>
        </section>

        {/* App Info */}
        <section className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">App Info</h2>
          <div className="trade-card space-y-3">

            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-foreground">Version</span>
                <p className="text-[11px] text-muted-foreground font-mono">
                  {APP_VERSION_LABEL} · Built {APP_BUILD_DATE}
                </p>
              </div>
              <span className="text-[10px] text-muted-foreground/60 font-mono">{APP_BUILD_HASH}</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-foreground">Clear Cache</span>
                <p className="text-[11px] text-muted-foreground">Force re-download all assets</p>
              </div>
              <ClearCacheButton />
            </div>
          </div>
        </section>




        <p className="text-center text-[10px] text-muted-foreground/50 pb-4">
          Trades Toolbelt — Built for the field
        </p>
      </div>
    </div>
  );
};

const ClearCacheButton = () => {
  const [clearing, setClearing] = useState(false);

  const handleClear = async () => {
    setClearing(true);
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      const regs = await navigator.serviceWorker?.getRegistrations();
      if (regs) await Promise.all(regs.map((r) => r.unregister()));
      toast.success("Cache cleared — reloading…");
      setTimeout(() => window.location.reload(), 800);
    } catch {
      toast.error("Could not clear cache");
      setClearing(false);
    }
  };

  return (
    <button
      onClick={handleClear}
      disabled={clearing}
      className="px-4 py-2 rounded-lg bg-destructive/15 text-destructive text-sm font-semibold hover:bg-destructive/25 transition-colors disabled:opacity-50"
    >
      {clearing ? "Clearing…" : "Clear"}
    </button>
  );
};

const HelpLink = () => {
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-between">
      <div>
        <span className="text-sm font-medium text-foreground">Help & FAQ</span>
        <p className="text-[11px] text-muted-foreground">Tool guides, tips & common questions</p>
      </div>
      <button
        onClick={() => navigate("/help")}
        className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-secondary text-sm font-semibold text-foreground hover:bg-secondary/80 transition-colors"
      >
        <HelpCircle className="w-4 h-4" />
        View
      </button>
    </div>
  );
};

const SettingRow = ({ label, description, children }: { label: string; description: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between gap-4">
    <div className="min-w-0">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <p className="text-[11px] text-muted-foreground">{description}</p>
    </div>
    {children}
  </div>
);

export default Settings;
